import cn from 'classnames'
import { useState } from 'react'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button/Button'
import { Input } from 'core-components/library/inputs'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { CockpitSegmentedControlAccent } from '../../../components/cockpitSegmentedControlAccent'
import type { IDocumentsNavigatorNode, TDocumentsGrouping } from '../../types'
import { DocumentsNavigatorTree } from '../documentsNavigatorTree/DocumentsNavigatorTree'

import styles from './DocumentsNavigator.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks
const MODES: TDocumentsGrouping[] = ['geo', 'creator', 'partner']
interface IProps {
  nodes: IDocumentsNavigatorNode[]
  grouping: TDocumentsGrouping
  onGroupingChange: (grouping: TDocumentsGrouping) => void
  selectedKey: string | null
  onSelect: (key: string | null) => void
  totalCount: number
}
export const DocumentsNavigator = ({
  nodes,
  grouping,
  onGroupingChange,
  selectedKey,
  onSelect,
  totalCount,
}: IProps) => {
  const t = useSafetyTranslation()
  const [search, setSearch] = useState('')
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set())
  const toggle = (key: string) =>
    setExpandedKeys((current) => {
      const next = new Set(current)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  return (
    <nav className={styles.side}>
      <CockpitSegmentedControlAccent className={styles.modes}>
        <SegmentedControl
          isStreatched
          useItemKeys
          selectedItem={grouping}
          onItemSelect={(next: string) => {
            setSearch('')
            setExpandedKeys(new Set())
            onGroupingChange(next as TDocumentsGrouping)
          }}
          size="small"
          variant="veneer"
        >
          {MODES.map((mode) => (
            <SegmentedControl.Item key={mode} itemKey={mode}>
              <Translation span text={KEYS.grouping[mode]} />
            </SegmentedControl.Item>
          ))}
        </SegmentedControl>
      </CockpitSegmentedControlAccent>
      <Input
        classes={{ root: styles.search }}
        value={search}
        onChange={setSearch}
        startContent={<i className="kbase kbase-search" />}
        placeholder={t(KEYS.navigation.search)}
      />
      <Button
        variant="text"
        className={cn(styles.root, { [styles.selected]: !selectedKey })}
        onClick={() => onSelect(null)}
      >
        <span className={styles.rootContent}>
          <Translation span text={KEYS.navigation.root} />
          <span>{totalCount}</span>
        </span>
      </Button>
      <DocumentsNavigatorTree
        nodes={nodes}
        query={search.trim().toLowerCase()}
        selectedKey={selectedKey}
        expandedKeys={expandedKeys}
        onToggle={toggle}
        onSelect={(key) => {
          setExpandedKeys((current) => new Set([...current, key]))
          onSelect(key)
        }}
      />
    </nav>
  )
}
